import { ZfLoadingSpinner, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useAdvisoryNeeds,
    useAdvisoryNeedsForTalkingPoint,
    useCreateTalkingPoint,
    useEnabledDomiciles,
    useReferenceTreeForTalkingPoint,
    useUpdateTalkingPoint,
} from '../../../api';
import type { AdvisoryNeedDto, DomicileDto, TalkingPointDto, TalkingPointRequestDto, TalkingPointUpdateDto } from '../../../api/generated/client';
import type { ICreatePoint, IUpdatePoint } from '../../../interfaces/topic';
import { MultiLanguageEditor } from '../../atoms';
import { DomicileSelection } from '../domicile-selection';
import { ListEditorDnd } from '../drag-and-drop';
import { TalkingPointUsages } from './TalkingPointUsages';

interface IProps {
    point?: TalkingPointDto;
}

export const TalkingPointEditor: FunctionComponent<IProps> = ({ point: selectedTalkingPoint }) => {
    const { t } = useTranslation();
    const { data: availableDomiciles } = useEnabledDomiciles();
    const { data: usedInTopic } = useReferenceTreeForTalkingPoint(selectedTalkingPoint?.id);
    const { data: allAdvisoryNeeds } = useAdvisoryNeeds();
    const { data: selectedAdvisoryNeeds } = useAdvisoryNeedsForTalkingPoint(selectedTalkingPoint?.id);

    const { mutate: createTalkingPoint } = useCreateTalkingPoint();
    const { mutate: updateTalkingPoint } = useUpdateTalkingPoint();

    const hasChanges = useRef(false);
    const [talkingPoint, setTalkingPoint] = useState<TalkingPointDto | undefined>(selectedTalkingPoint);
    const [advisoryNeeds, setAdvisoryNeeds] = useState(selectedAdvisoryNeeds);

    useEffect(() => setTalkingPoint(selectedTalkingPoint), [selectedTalkingPoint]);
    useEffect(() => setAdvisoryNeeds(selectedAdvisoryNeeds), [selectedAdvisoryNeeds]);

    const handleDomicileChange = (includedDomiciles: DomicileDto[], excludedDomiciles: DomicileDto[], global: boolean) => {
        hasChanges.current = true;
        setTalkingPoint((prevState) => ({
            ...prevState!,
            global,
            excludedDomiciles,
            includedDomiciles,
        }));
    };

    const handleAdvisoryNeedsChange = (needs: AdvisoryNeedDto[]) => {
        hasChanges.current = true;
        setAdvisoryNeeds([...needs]);
    };

    const handleTalkingPointChange = (property: keyof TalkingPointDto) => (value: TalkingPointDto[keyof TalkingPointDto]) =>
        setTalkingPoint((prevState) => {
            hasChanges.current = true;

            return { ...prevState!, [property]: value };
        });

    const isTalkingPointToUpdate = (point?: ICreatePoint | IUpdatePoint | TalkingPointDto): point is IUpdatePoint =>
        !!point && Object.prototype.hasOwnProperty.call(point, 'id') && (point as IUpdatePoint).id !== -1;

    const handleSaveChanges = () => {
        if (!talkingPoint?.name || !talkingPoint.description) return;

        const payload: TalkingPointRequestDto | TalkingPointUpdateDto = {
            ...talkingPoint,
            advisoryNeedIds: advisoryNeeds?.map((need: AdvisoryNeedDto) => need.id) ?? [],
        };

        if (isTalkingPointToUpdate(payload)) updateTalkingPoint(payload);
        else createTalkingPoint(payload);

        hasChanges.current = false;
    };

    if (!advisoryNeeds || !selectedAdvisoryNeeds) return <ZfLoadingSpinner />;

    return (
        <ZfStack direction="column" spacing="large">
            <ZfStack direction="row" alignment="center" justify="space-between">
                <ZfTypography variant="heading-small">
                    {!isTalkingPointToUpdate(talkingPoint)
                        ? t('contentAdmin.createNewTalkingPoint')
                        : `${t('contentAdmin.editTalkingPoint')}: ID ${talkingPoint?.id}`}
                </ZfTypography>
                <ZfTextButton hierarchy="secondary" disabled={!hasChanges.current} onClick={handleSaveChanges}>
                    {t('contentAdmin.saveChanges')}
                </ZfTextButton>
            </ZfStack>
            <MultiLanguageEditor label={t('roadmapColHeaderTitle')} value={talkingPoint?.name} direction="row" onChange={handleTalkingPointChange('name')} />
            {isTalkingPointToUpdate(talkingPoint) && <TalkingPointUsages usages={usedInTopic ?? []} />}
            <DomicileSelection
                global={!!talkingPoint?.global}
                onChange={handleDomicileChange}
                available={availableDomiciles ?? []}
                excludedIn={talkingPoint?.excludedDomiciles ?? []}
                includedIn={talkingPoint?.includedDomiciles ?? []}
            />
            <ListEditorDnd
                title={t('contentAdmin.advisoryNeeds')}
                selectedItems={selectedAdvisoryNeeds}
                allItems={allAdvisoryNeeds ?? []}
                onChange={handleAdvisoryNeedsChange}
            />
            <MultiLanguageEditor
                label={t('titleTpDescription')}
                value={talkingPoint?.description}
                onChange={handleTalkingPointChange('description')}
                multiline={true}
                markdown={true}
                collapsible={true}
                expanded={true}
            />
        </ZfStack>
    );
};
